<script lang="ts">
	// What the cashier reads out loud, as the body of the payment sheet once
	// the sale has gone through: the change, the largest figure on the screen.
	// The sheet's title carries the order number and its primary action is
	// Next sale.
	//
	// A card sale shows the total paid, as there is no change to read out.
	// A free order (UC-35) names its discount and says Free order instead.
	// Done follows a payment only: every order is sent before it is paid
	// (#348), and Send leaves the order on Sell.
	//
	// Payment queues the receipt in the order store. The line under the
	// figure follows its job until it prints or fails. When no receipt
	// printer is set up, it explains that a manager sets one up in Orders >
	// Printers, and Print again is disabled. An extra line reports when a cash
	// sale's receipt printer has no cash drawer.
	//
	// A receipt or a kick whose delivery is uncertain (#515) is held by the
	// store until someone decides. Its line says so, and its action becomes
	// the decision Done offers: Print again becomes Print another copy, one
	// REPRINT copy and no kick, and Retry becomes Open drawer now, for the
	// kick's 2 minutes. Done keeps at most two actions; "I have it" and "The
	// drawer opened" are offered in Orders, and Next sale leaves either one
	// undecided.

	import Button from '../components/Button.svelte';
	import { message, type Language } from '../i18n/messages';
	import { formatMinor } from '../format/money';

	/** Where the receipt is: the queue, not printed, or held for a decision (#515). */
	export type PrintPhase = 'idle' | 'printing' | 'failed' | 'uncertain';

	interface Props {
		language: Language;
		minorDigits: number;
		/** How the order was paid. */
		method: 'cash' | 'card';
		changeMinor: number;
		totalMinor: number;
		/** A receipt is queued or printing, or its cooldown runs. */
		printing: boolean;
		phase: PrintPhase;
		noReceiptPrinter?: boolean;
		noDrawer?: boolean;
		drawerPhase: 'idle' | 'watching' | 'failed' | 'uncertain';
		onDrawerRetry: () => void;
		/** A held kick is within its 2 minutes: Open drawer now is offered. */
		drawerNow?: boolean;
		onDrawerNow: () => void;
		onPrint: () => void;
		/** The held receipt's decision: one more copy, marked REPRINT. */
		onPrintAnotherCopy: () => void;
		/** Why the receipt's decision did not go through, said in place of its line. */
		refusal?: string;
		/** Why the kick's decision did not go through, said in place of its line. */
		drawerRefusal?: string;
		/**
		 * The discount made the order free (UC-35): the screen names the
		 * discount and says Free order in place of a figure.
		 */
		free?: { discount: string } | null;
	}

	let {
		language,
		minorDigits,
		method,
		changeMinor,
		totalMinor,
		printing,
		phase,
		noReceiptPrinter = false,
		noDrawer = false,
		drawerPhase,
		onDrawerRetry,
		drawerNow = false,
		onDrawerNow,
		onPrint,
		onPrintAnotherCopy,
		refusal = '',
		drawerRefusal = '',
		free = null,
	}: Props = $props();

	const label = $derived(
		free
			? free.discount
			: method === 'card'
				? message(language, 'donePaidByCard')
				: message(language, 'doneChange'),
	);
	const figure = $derived(method === 'cash' ? changeMinor : totalMinor);

	const status = $derived(
		noReceiptPrinter
			? message(language, 'printNoticeNoReceiptPrinter')
			: refusal
				? refusal
				: phase === 'printing'
					? message(language, 'donePrinting')
					: phase === 'failed'
						? message(language, 'doneNotPrinted')
						: phase === 'uncertain'
							? message(language, 'printHeldReceipt')
							: '',
	);

	const drawerLine = $derived(
		drawerRefusal
			? drawerRefusal
			: drawerPhase === 'failed'
				? message(language, 'doneDrawerFailed')
				: drawerPhase === 'uncertain'
					? message(language, 'printHeldDrawer')
					: noDrawer
						? message(language, 'printNoticeNoDrawer')
						: '',
	);
</script>

<div class="done" data-testid="panel-done">
	<div class="read">
		<span class="label" data-testid="done-label">{label}</span>
		{#if free}
			<output class="free" data-testid="done-free">{message(language, 'doneFree')}</output>
		{:else}
			<output class="figure" dir="ltr" data-testid="done-change">
				{formatMinor(figure, minorDigits)}
			</output>
		{/if}
	</div>
	<div role="status" class="status-wrap">
		<p class="status" data-testid="done-status">{status}</p>
		{#if drawerLine}
			<p class="status drawer-status" data-testid="done-drawer-status">{drawerLine}</p>
		{/if}
	</div>
	<div class="action-wrap">
		{#if drawerPhase === 'failed'}
			<Button
				testId="done-drawer-retry"
				label={message(language, 'doneRetry')}
				tone="secondary"
				size="lg"
				onPress={onDrawerRetry}
			/>
		{:else if drawerPhase === 'uncertain' && drawerNow}
			<Button
				testId="done-drawer-now"
				label={message(language, 'printOpenDrawerNow')}
				tone="secondary"
				size="lg"
				onPress={onDrawerNow}
			/>
		{/if}
		{#if phase === 'uncertain'}
			<Button
				testId="done-print-another"
				icon="printer"
				label={message(language, 'printAnotherCopy')}
				tone="secondary"
				size="lg"
				disabled={printing || noReceiptPrinter}
				onPress={onPrintAnotherCopy}
			/>
		{:else}
			<Button
				testId="done-print-again"
				icon="printer"
				label={message(language, 'donePrintAgain')}
				tone="secondary"
				size="lg"
				disabled={printing || noReceiptPrinter}
				onPress={onPrint}
			/>
		{/if}
	</div>
</div>

<style>
	.done {
		flex: 1;
		min-block-size: 0;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: var(--space-9);
		text-align: center;
	}

	.read {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: var(--space-2);
	}

	.label {
		font-size: var(--text-md);
		color: var(--text-muted);
	}

	.figure {
		font-family: var(--font-mono);
		font-size: var(--figure-xl);
		font-weight: 700;
		line-height: 1;
		letter-spacing: var(--tracking-tight);
	}

	.free {
		font-size: var(--figure-lg);
		font-weight: 700;
		line-height: 1;
		letter-spacing: var(--tracking-tight);
		white-space: nowrap;
	}

	.status-wrap {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: var(--space-2);
		min-block-size: 24px;
	}

	.status {
		margin: 0;
		max-inline-size: var(--measure);
		font-size: var(--text-sm);
		font-weight: 600;
		color: var(--text-strong);
	}

	.action-wrap {
		display: flex;
		gap: var(--touch-gap);
		flex-wrap: wrap;
		justify-content: center;
	}
</style>
